import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChatPanel } from './ChatPanel';
import { Confetti } from './Confetti';
import { CountdownStage } from './CountdownStage';
import { WinnersList } from './WinnersList';
import { useCountdown } from '../hooks/useCountdown';
import { browserTimeZone } from '../utils/schedule';
import type { Session } from '../types/session';
import type { useRoom } from '../hooks/useRoom';
interface ParticipantViewProps {
  session: Session;
  roomApi: ReturnType<typeof useRoom>;
}
type MobileTab = 'winners' | 'chat';
export function ParticipantView({
  session,
  roomApi
}: ParticipantViewProps) {
  const {
    room,
    participants,
    winners
  } = roomApi;
  const [mobileTab, setMobileTab] = useState<MobileTab>('winners');
  const closed = room.closed;
  const winner = room.lastWinner;
  const mine = session.name.trim().toLowerCase();
  const youWon = winner?.name.trim().toLowerCase() === mine;
  const remaining = useCountdown(room.startsAt);
  const pending = !closed && room.startsAt !== null && remaining.total > 0;
  const joined = participants.filter(p => !p.blocked).length;

  // Before the scheduled start there is no room to talk in yet — the countdown
  // takes the whole screen and chat only appears once the event goes live.
  if (pending && room.startsAt !== null) {
    return <main className="flex flex-1" data-id="element-272" data-mp="2rbzdr5zpley1">
        <CountdownStage eventName={room.name} startsAt={room.startsAt} timeZone={room.timeZone ?? browserTimeZone()} footnote={`You are in as ${session.name} · ${joined} people waiting · chat opens when the event starts`} data-id="element-273" data-mp="2rf3sgxasvt2y" />
      </main>;
  }
  const chat = <ChatPanel session={session} room={room} messages={roomApi.messages} participants={roomApi.participants} me={roomApi.me} onSend={roomApi.sendMessage} onDeleteMessage={roomApi.deleteMessage} onWipeChat={roomApi.wipeChat} onSetChatEnabled={roomApi.setChatEnabled} onSetMuted={roomApi.setMuted} onSetBlocked={roomApi.setBlocked} data-id="element-274" data-mp="28msthej9ofa4" />;
  return <main className="flex flex-1 flex-col lg:grid lg:grid-cols-[1fr_22rem] lg:items-stretch xl:grid-cols-[1fr_25rem]" data-id="element-275" data-mp="1asqe1wli6gxs">
      {/* Stage */}
      <div className="relative flex min-w-0 flex-col items-center px-5 py-8 text-center sm:px-10 sm:py-12" data-id="element-276" data-mp="15uovdcrzojmu">
        {winner && !closed && <Confetti key={winner.at} data-id="element-277" data-mp="10t9bvp5gml6k" />}

        <p className={`text-[0.65rem] font-semibold uppercase tracking-[0.24em] sm:text-[0.7rem] sm:tracking-[0.28em] ${closed ? 'text-muted' : 'text-gold'}`} data-id="element-278" data-mp="1yj4b61iy35z8">
          {closed ? 'Event closed' : winner ? youWon ? 'You won' : 'Latest winner' : 'Draw in progress'}
        </p>

        {closed ? <>
            <h2 className="mt-4 font-display text-[clamp(1.75rem,7vw,4.5rem)] leading-tight text-white sm:mt-6" data-id="element-279" data-mp="d6t0mdlzd1sp">
              This event has ended
            </h2>
            <p className="mt-3 max-w-md text-sm text-muted" data-id="element-280" data-mp="2jute84ygpqcj">
              The host closed {room.name}. You can still read the chat and the full
              winners list.
            </p>
          </> : winner ? <motion.h2 key={winner.at} initial={{
        opacity: 0,
        scale: 0.96,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} transition={{
        duration: 0.28,
        ease: [0.23, 1, 0.32, 1]
      }} className="mt-4 break-words font-display text-[clamp(2.25rem,9vw,6.5rem)] leading-[1.02] text-white sm:mt-6" data-id="element-281" data-mp="2tqn0wh5wb081">
            {winner.name}
          </motion.h2> : <h2 className="mt-4 font-display text-[clamp(1.75rem,6vw,3.5rem)] leading-tight text-white sm:mt-6" data-id="element-282" data-mp="3cgmmuv6fvz78">
            Waiting for the host to spin
          </h2>}

        {!closed && <p className="mt-4 text-xs text-muted sm:mt-5 sm:text-sm" data-id="element-283" data-mp="1i6rc81ncm4ro">
            You are in as <span className="text-white" data-id="element-284" data-mp="1n5lt2y1r9j95">{session.name}</span> · {joined}{' '}
            people in the room
          </p>}

        {/* Desktop keeps the winners list under the stage. */}
        <div className="mt-12 hidden w-full max-w-xl text-left lg:block" data-id="element-285" data-mp="38la0do2bfvgw">
          <WinnersList winners={winners} mine={mine} className="max-h-[34vh]" data-id="element-286" data-mp="3mk02xj7wmncn" />
        </div>
      </div>

      {/* Mobile / tablet: one tabbed panel so winners and chat are both reachable. */}
      <div className="flex min-h-[60vh] flex-col border-t border-line px-5 pb-5 pt-3 sm:px-8 lg:hidden" data-id="element-287" data-mp="2uv6kdeu14xzw">
        <div className="flex gap-5 border-b border-line" role="tablist" data-id="element-288" data-mp="2pwbslne33z3n">
          {(['winners', 'chat'] as MobileTab[]).map(t => <button key={t} type="button" role="tab" aria-selected={mobileTab === t} onClick={() => setMobileTab(t)} className={`-mb-px border-b-2 pb-2.5 text-sm font-semibold transition-colors duration-150 ease-emphasis focus-visible:outline focus-visible:outline-1 focus-visible:outline-gold ${mobileTab === t ? 'border-gold text-white' : 'border-transparent text-muted hover:text-body'}`} data-id="element-289" data-mp="33o9yzqgevgw4">
              {t === 'winners' ? `Winners${winners.length ? ` · ${winners.length}` : ''}` : `Chat${roomApi.messages.length ? ` · ${roomApi.messages.length}` : ''}`}
            </button>)}
        </div>

        <div className="mt-3 flex min-h-0 flex-1 flex-col" data-id="element-290" data-mp="2fp9bfc4b4yq2">
          {mobileTab === 'winners' ? <WinnersList winners={winners} mine={mine} showHeading={false} className="flex-1" data-id="element-291" data-mp="25sxv8b1ljoqc" /> : chat}
        </div>
      </div>

      {/* Desktop chat column */}
      <div className="hidden flex-col overflow-hidden border-l border-line px-5 py-5 sm:px-7 lg:flex lg:h-[calc(100vh-7.5rem)]" data-id="element-292" data-mp="uc7mol9paktk">
        <h2 className="pb-3 text-lg font-semibold text-white" data-id="element-293" data-mp="zahj0z75vrgr">Room chat</h2>
        {chat}
      </div>
    </main>;
}